import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import {
    ExceptionRateRule,
    ExceptionRatesFrom,
    ExceptionSaveFormat,
    ExceptionSettlementMode,
    InRateZeroableReason,
    describeRetroactiveExceptionSave,
    isHiddenInMode,
    formatExceptionRule,
    useSaveCoachExceptionRates,
} from '@/features/coachCommissionTable';
import { getSettlementClassificationErrorMessage } from '@/features/settlementClassification';
import DialogShell from './DialogShell';

type CoachOption = { coachId: string; coachName: string };
type Result = { coachId: string; ok: boolean; message?: string };

type Props = {
    centerId: string;
    year: string;
    month: string;
    sourceCoachName: string;
    rules: ExceptionRateRule[];
    coaches: CoachOption[];
    categoryName: (key: string) => string;
    /** 요약 문구용. 생략하면 상세 정산. 복사는 방식과 무관하게 규칙 전체(숨긴 규칙·기본 정산 전용 값 포함)를 그대로 보낸다. */
    settlementMode?: ExceptionSettlementMode;
    /** 저장 형식. 'legacy' = 이전 서버(예전 형식 그대로). 'v3' = 「시간 상관없이 IN 요율」을 아는 서버. */
    saveFormat?: ExceptionSaveFormat;
    /** 상세 정산: 강사의 이 달 IN 구간표가 0원이 될 수 있는 이유(서버 inRateZeroable). 없으면 null. */
    zeroableOf?: (coachId: string) => InRateZeroableReason | null;
    onClose: () => void;
    onApplied: (coachId: string, rules: ExceptionRateRule[], from: ExceptionRatesFrom | null) => void;
};

/**
 * 같은 예외를 다른 강사에게 적용 — 일괄 API 가 없어 강사마다 PUT 을 한 번씩 보낸다.
 * 순서대로 보내고, 실패한 강사는 이유와 함께 남긴다(성공한 강사는 이미 저장됨).
 * 「이전 달 설정 복사」 대화상자(CopyFromPrevMonthDialog)와 같은 틀이다.
 */
const CopyExceptionRatesDialog = ({
    centerId,
    year,
    month,
    sourceCoachName,
    rules,
    coaches,
    categoryName,
    settlementMode = 'tier',
    saveFormat = 'v2',
    zeroableOf,
    onClose,
    onApplied,
}: Props) => {
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [step, setStep] = useState<'pick' | 'confirm' | 'running' | 'done'>('pick');
    const [results, setResults] = useState<Result[]>([]);
    const save = useSaveCoachExceptionRates();

    const monthLabel = `${Number(year)}년 ${Number(month)}월`;
    // 지난 달에 적용하면 그 달~지난달 급여가 다시 계산된다 — 확인 단계에서 함께 알린다.
    const retroNotice = describeRetroactiveExceptionSave(
        { year, month },
        { year: dayjs().year(), month: dayjs().month() + 1 },
    );
    const allSelected = coaches.length > 0 && selected.size === coaches.length;
    const nameOf = (coachId: string) => coaches.find((c) => c.coachId === coachId)?.coachName ?? '강사';

    const toggle = (coachId: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(coachId)) next.delete(coachId);
            else next.add(coachId);
            return next;
        });

    const run = async () => {
        setStep('running');
        const out: Result[] = [];
        for (const coach of coaches.filter((c) => selected.has(c.coachId))) {
            try {
                const res = await save.mutateAsync({
                    centerId,
                    body: { coachId: coach.coachId, year, month, exceptionRates: rules },
                    format: saveFormat,
                });
                onApplied(coach.coachId, res?.exceptionRates ?? rules, res?.exceptionRatesFrom ?? { year, month });
                out.push({ coachId: coach.coachId, ok: true });
            } catch (e) {
                out.push({ coachId: coach.coachId, ok: false, message: getSettlementClassificationErrorMessage(e as any) });
            }
            setResults([...out]);
        }
        setStep('done');
    };

    const failed = results.filter((r) => !r.ok);
    // 「항상 IN 요율의 배수」를 IN 요율이 0일 수 있는 강사에게 거는가 — 확인 단계에서 이름을 알린다
    const zeroableTargets =
        zeroableOf && rules.some((rule) => rule.mode === 'inMultiplier')
            ? coaches.filter((c) => selected.has(c.coachId) && !!zeroableOf(c.coachId))
            : [];

    return (
        <DialogShell title="다른 강사에게 같은 예외 적용" width={520} onClose={onClose} busy={step === 'running'}>
            <Col className="px-[24px] pb-[22px] overflow-y-auto">
                <Col className="rounded-[10px] bg-[#F6F8FA] px-[14px] py-[10px] mt-[6px]">
                    <DemiLightText fontSize={13} color="#6B7280">
                        {`${sourceCoachName} 강사의 예외 수업료`}
                    </DemiLightText>
                    {rules
                        .filter((rule) => !isHiddenInMode(rule, settlementMode))
                        .map((rule, index) => (
                            <RegularText key={index} fontSize={14} lineHeight={21} color="#3D4244" className="mt-[2px]">
                                {`· ${formatExceptionRule(rule, categoryName, settlementMode)}`}
                            </RegularText>
                        ))}
                    {settlementMode !== 'tier' && rules.some((rule) => rule.mode === 'inMultiplier') && (
                        <DemiLightText fontSize={13} lineHeight={19} color="#6B7280" className="mt-[2px]">
                            상세 정산에서만 계산되는 예외도 함께 적용돼요.
                        </DemiLightText>
                    )}
                </Col>

                {step === 'pick' && (
                    <>
                        <Row className="items-center justify-between mt-[18px] mb-[6px]">
                            <MediumText fontSize={15} color="#1A1A1A">
                                누구에게
                            </MediumText>
                            <RegularText
                                fontSize={13}
                                color={colors.primary}
                                className="cursor-pointer"
                                onClick={() =>
                                    setSelected(allSelected ? new Set() : new Set(coaches.map((c) => c.coachId)))
                                }
                            >
                                {allSelected ? '모두 해제' : '모두 선택'}
                            </RegularText>
                        </Row>
                        {coaches.length === 0 ? (
                            <RegularText fontSize={14} color="#6B7280" className="py-[12px]">
                                적용할 다른 강사가 없어요.
                            </RegularText>
                        ) : (
                            <Col className="max-h-[300px] overflow-y-auto">
                                {coaches.map((coach) => (
                                    <label
                                        key={coach.coachId}
                                        className="flex items-center gap-[10px] px-[10px] py-[9px] rounded-[10px] cursor-pointer hover:bg-[#F2F4F6]"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected.has(coach.coachId)}
                                            onChange={() => toggle(coach.coachId)}
                                            style={{ width: 18, height: 18, accentColor: '#3D73DD' }}
                                        />
                                        <RegularText fontSize={15} color="#3D4244">
                                            {coach.coachName}
                                        </RegularText>
                                    </label>
                                ))}
                            </Col>
                        )}
                    </>
                )}

                {step === 'confirm' && (
                    <RegularText fontSize={15} lineHeight={23} color="#3D4244" className="mt-[18px] whitespace-pre-line">
                        {`${coaches
                            .filter((c) => selected.has(c.coachId))
                            .map((c) => c.coachName)
                            .join(', ')} (${selected.size}명)\n${monthLabel} 예외 수업료를 이 규칙으로 바꿔요.\n정해 둔 예외는 덮어쓰고, ${monthLabel} 급여 계산도 바뀌어요.`}
                    </RegularText>
                )}
                {step === 'confirm' && zeroableTargets.length > 0 && (
                    <RegularText fontSize={14} lineHeight={21} color="#B45309" className="mt-[8px] break-keep">
                        {`${zeroableTargets
                            .map((c) => c.coachName)
                            .join(', ')} 강사는 매출 구간에 따라 IN 요율이 0일 수 있어요. 그 구간에서는 「항상 IN 요율의 배수」 수업이 0원이 돼요.`}
                    </RegularText>
                )}
                {step === 'confirm' && retroNotice && (
                    <RegularText fontSize={14} lineHeight={21} color="#B45309" className="mt-[8px] break-keep">
                        {retroNotice}
                    </RegularText>
                )}

                {(step === 'running' || step === 'done') && (
                    <Col className="mt-[16px] gap-[6px]">
                        <RegularText fontSize={14} color="#6B7280">
                            {step === 'running'
                                ? `적용하는 중… (${results.length}/${selected.size})`
                                : failed.length === 0
                                  ? `${results.length}명 모두 적용했어요.`
                                  : `${results.length - failed.length}명 적용, ${failed.length}명 실패했어요. 실패한 강사는 바뀌지 않았어요.`}
                        </RegularText>
                        {results.map((r) => (
                            <Row key={r.coachId} className="items-start gap-[8px]">
                                <RegularText fontSize={14} color={r.ok ? '#12A25D' : '#D93043'} className="w-[34px]">
                                    {r.ok ? '완료' : '실패'}
                                </RegularText>
                                <RegularText fontSize={14} color="#3D4244" className="w-[90px]">
                                    {nameOf(r.coachId)}
                                </RegularText>
                                {!r.ok && (
                                    <RegularText fontSize={13} lineHeight={19} color="#6B7280" className="flex-1">
                                        {r.message}
                                    </RegularText>
                                )}
                            </Row>
                        ))}
                    </Col>
                )}

                <Row className="justify-end gap-[8px] mt-[20px]">
                    {step !== 'done' && (
                        <div
                            className="h-[40px] w-[88px] rounded-[9px] bg-[#F4F5F7] flex items-center justify-center cursor-pointer"
                            onClick={step === 'running' ? undefined : step === 'confirm' ? () => setStep('pick') : onClose}
                        >
                            <RegularText fontSize={15} color="#3D4244">
                                {step === 'confirm' ? '이전' : '취소'}
                            </RegularText>
                        </div>
                    )}
                    {step === 'pick' && (
                        <div
                            className={`h-[40px] px-[18px] rounded-[9px] bg-primary flex items-center justify-center ${
                                selected.size === 0 ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            onClick={selected.size === 0 ? undefined : () => setStep('confirm')}
                        >
                            <RegularText fontSize={15} color="white">
                                {selected.size === 0 ? '강사를 골라 주세요' : `${selected.size}명에게 적용`}
                            </RegularText>
                        </div>
                    )}
                    {step === 'confirm' && (
                        <div
                            className="h-[40px] px-[18px] rounded-[9px] bg-primary flex items-center justify-center cursor-pointer"
                            onClick={run}
                        >
                            <RegularText fontSize={15} color="white">
                                적용하기
                            </RegularText>
                        </div>
                    )}
                    {step === 'done' && (
                        <div
                            className="h-[40px] w-[88px] rounded-[9px] bg-primary flex items-center justify-center cursor-pointer"
                            onClick={onClose}
                        >
                            <RegularText fontSize={15} color="white">
                                닫기
                            </RegularText>
                        </div>
                    )}
                </Row>
            </Col>
        </DialogShell>
    );
};

export default CopyExceptionRatesDialog;
